import { DeadlineTime } from "@/components/homepage/RoundCard/components/DeadlineTime";
import { Separator } from "@/components/ui/separator";
import { Skeleton } from "@/components/ui/skeleton";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { POLL_INTERVAL } from "@/constants";
import { useHandleError } from "@/hooks/useHandleError";
import { getAuthenticatedUser } from "@/store/auth.store";
import { useEntryStore } from "@/store/entry.store";
import { pointsToYen } from "@/utils/pointsToYen";
import { CalendarIcon, GroupIcon, InfoPlayerIcon, ThumbaCoin, ThumbaTrophyIcon } from "@roman/shared/components/Icons";
import { useSchedule } from "@roman/shared/hooks/soccer/useSchedule";
import type { RoundDetails } from "@roman/shared/types/RoundDetails";
import type { RoundEntryUnit } from "@roman/shared/types/RoundEntryUnit";
import type { ScheduledEntry } from "@roman/shared/types/Schedule";
import { cn } from "@roman/shared/utils";
import { type HTMLAttributes, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { RoundDetailPlayButtonText } from "../Rounds/constant";
import { SchedulePlayersTab } from "./SchedulePlayersTab";
import { ScheduleUsersTab } from "./ScheduleUsersTab";

export const SchedulePage = () => {
  const [selectedPlayersMap, setSelectedPlayersMap] = useState<Record<string, boolean>>({});
  const [selectedEntry, setSelectedEntry] = useState<ScheduledEntry | null>(null);
  const [currentTab, setCurrentTab] = useState("players");
  const { roundEntryUnitId } = useParams<{ roundEntryUnitId: string }>();
  const navigate = useNavigate();
  const resetEntryData = useEntryStore((state) => state.resetAll);

  const tabClassName =
    "w-1/2 p-2 h-4 bg-white h-[50px] text-lg font-bold text-primary rounded-none border border-primary data-[state=active]:bg-primary data-[state=active]:text-white data-[state=active]:shadow-none";
  const tabLeftClassName = "rounded-l-md";
  const tabRightclassName = "rounded-r-md";
  const user = getAuthenticatedUser();

  if (!roundEntryUnitId) {
    return <div>Error: Invalid roundEntryUnitId</div>;
  }

  const [searchParams] = useSearchParams();
  useEffect(() => {
    const tab = searchParams.get("tab");
    if (tab === "players" || tab === "users") {
      setCurrentTab(tab);
    }
  }, [searchParams]);

  const { roundEntryUnit, round, entries, isLoading, totalEntries } = useSchedule(
    roundEntryUnitId,
    user.id,
    useHandleError("スケジュールの取得に失敗しました"),
    "ja", // Implement language provider later
    POLL_INTERVAL,
  );

  useEffect(() => {
    if (entries.length) {
      setSelectedEntry(entries[0]);
    }
  }, [entries]);

  const selectedPlayers = useMemo(() => {
    if (!round) {
      return [];
    }

    const result = [];
    for (const i in round.players.homePlayers) {
      const homePlayer = round.players.homePlayers[i];
      const awayPlayer = round.players.awayPlayers[i];
      const selectPlayer = selectedPlayersMap[homePlayer.id] ? homePlayer : awayPlayer;

      if (selectPlayer) {
        result.push(selectPlayer);
      }
    }

    return result;
  }, [selectedPlayersMap, round]);

  const captain = useMemo(
    () => selectedPlayers.find((player) => player.id === selectedEntry?.selectedCaptains?.captainPlayerId),
    [selectedPlayers, selectedEntry],
  );

  const viceCaptain = useMemo(() => {
    return selectedPlayers.find(
      (player) =>
        selectedEntry?.selectedCaptains.type === "0002" &&
        player.id === selectedEntry?.selectedCaptains?.viceCaptainPlayerId,
    );
  }, [selectedPlayers, selectedEntry]);

  useEffect(() => {
    if (!selectedEntry) {
      return;
    }

    const newSelectedPlayersMap: Record<string, boolean> = {};
    for (const playerId of selectedEntry.selectedPlayers) {
      newSelectedPlayersMap[playerId] = true;
    }

    setSelectedPlayersMap(newSelectedPlayersMap);
  }, [selectedEntry]);

  if (isLoading || !roundEntryUnit || !round) {
    return <LoadingSkeleton />;
  }

  const handleGoToRoundDetail = () => {
    resetEntryData();
    navigate(`/rounds/${round.id}`, {
      state: { roundDetailPlayButtonText: RoundDetailPlayButtonText.AddEntry },
    });
  };

  return (
    <div className="h-screen bg-white">
      <button
        type="button"
        className="w-full flex justify-center items-center py-2.5 bg-[#D1E1FC] space-x-3 cursor-pointer"
        onClick={handleGoToRoundDetail}
      >
        <div className="font-semibold text-xs whitespace-nowrap overflow-hidden text-primary flex space-x-1">
          <span>{round.homeName}</span>
          <span>vs</span>
          <span>{round.awayName}</span>
        </div>
        <InfoPlayerIcon className="[&_circle]:fill-white" />
      </button>
      <RoundInformation round={round} roundEntryUnit={roundEntryUnit} totalEntries={totalEntries} />

      <div className="w-full px-2 md:px-2 lg:px-6 xl:px-6 2xl:px-6 bg-white pb-1 xl:pb-6 2xl:pb-6">
        <Tabs
          value={currentTab}
          onValueChange={(value) => {
            navigate(`/scheduled/${roundEntryUnitId}?tab=${value}`);
            setCurrentTab(value);
          }}
        >
          <TabsList className="w-full p-0 rounded-none">
            <TabsTrigger className={cn(tabClassName, tabLeftClassName)} value="players">
              選手
            </TabsTrigger>
            <TabsTrigger className={cn(tabClassName, tabRightclassName)} value="users">
              ユーザー
            </TabsTrigger>
          </TabsList>

          <TabsContent value="players">
            {captain && selectedPlayers && (
              <SchedulePlayersTab
                captain={captain}
                viceCaptain={viceCaptain}
                round={round}
                roundEntryUnitId={roundEntryUnitId}
                entryId={selectedEntry?.id || ""}
                selectedPlayers={selectedPlayers.map((player) => player.id)}
              />
            )}
          </TabsContent>

          <TabsContent value="users">
            <ScheduleUsersTab
              round={round}
              entries={entries}
              user={user}
              onClick={(entry) => {
                setCurrentTab("players");
                setSelectedEntry(entry);
              }}
            />
          </TabsContent>
        </Tabs>
        <div
          className={cn(
            "w-full text-xl font-bold text-white rounded-lg text-center my-4 py-[10px] cursor-pointer bg-primary",
          )}
          onClick={() => {
            // Reset selected players data because selected players data kept when not login.
            resetEntryData();
            navigate(`/rounds/${round.id}/select-players`);
          }}
        >
          追加でエントリーする
        </div>
      </div>
    </div>
  );
};

type RoundInformationProps = {
  round: RoundDetails;
  roundEntryUnit: RoundEntryUnit;
  totalEntries: number;
};

export const RoundInformation: React.FC<RoundInformationProps> = (props: RoundInformationProps) => {
  const { round, roundEntryUnit } = props;
  return (
    <div className="flex flex-col bg-white rounded-md text-base w-full pb-6">
      <div className=" px-2 md:px-2 lg:px-6 xl:px-6 2xl:px-6 ">
        <div className="relative rounded-sm w-full p-1">
          <div className="flex items-center space-x-2">
            <ThumbaTrophyIcon className="min-w-6 min-h-6" />
            <div className="flex items-center">
              <span className="text-sm">賞金総額：</span>
              <span className="font-bold text-xl">{pointsToYen(roundEntryUnit.prizePool)}</span>
              <span className="font-bold text-sm">円</span>
            </div>
          </div>
          <SelectionBadge type={round.type} />
        </div>
        <RoundInfoFooter {...props} />
      </div>
      <Separator orientation="horizontal" className="bg-[#D1E1FC] h-[2px]" />
    </div>
  );
};

const RoundInfoFooter = ({
  round,
  roundEntryUnit,
  totalEntries,
}: {
  round: RoundDetails;
  roundEntryUnit: RoundEntryUnit;
  totalEntries: number;
}) => {
  return (
    <div className="flex flex-col justify-between p-1">
      <div className="flex items-center space-x-2">
        <ThumbaCoin className="h-[22px] w-[22px]" />
        <div>
          <span className="text-base font-bold">{roundEntryUnit.entryFee}pt</span>
          <span className="text-sm">/1回</span>
        </div>
      </div>
      <div className="flex justify-end mt-2 gap-2">
        <div className="flex items-center">
          <GroupIcon className="text-disabled" />
          <div className="flex flex-col px-2">
            <span className="text-dark-gray text-[10px] whitespace-nowrap leading-tight">エントリー数</span>
            <span className="font-bold text-sm">
              {totalEntries}/{roundEntryUnit.maxNumEntries}
            </span>
          </div>
        </div>

        <div className={cn("flex items-center")}>
          <CalendarIcon className="text-disabled" />
          <div className="flex flex-col px-2">
            <span className="text-dark-gray text-[10px] whitespace-nowrap leading-tight">開催日</span>

            <DeadlineTime entryCloseTime={round.entryCloseTime} />
          </div>
        </div>
      </div>
    </div>
  );
};

const SelectionBadge = ({ type }: { type: string }) => {
  const description = type === "Round0002" ? "初級者" : "中級者";
  const textColor = type === "Round0002" ? "text-light-green" : "text-orange";
  const bgColor = type === "Round0002" ? "bg-light-green" : "bg-orange";
  const borderColor = type === "Round0002" ? "border-[#74BC00]" : "border-[#FF7300]";

  return (
    <div className="absolute top-[5px] right-[5px] flex flex-row-reverse whitespace-nowrap items-center">
      <div className={cn("flex rounded-full text-white items-center w-full border border-solid", borderColor)}>
        <div className={cn("relative flex justify-center  bg-white rounded-l-full w-1/2 ", textColor)}>
          <span className="py-[2px] font-bold text-center text-xs px-4">{type === "Round0002" ? "5" : "11"}人制</span>
          <div
            className="bg-white absolute -right-[5px]"
            style={{
              clipPath: "polygon(0 0, 100% 0, 0% 100%, 0 100%)",
              width: 12,
              height: "100%",
              zIndex: 99,
            }}
          />
        </div>

        <div
          className={cn(
            "relative flex justify-center pl-2 pr-4 text-xs font-bol rounded-r-full w-1/2 text-center",
            bgColor,
          )}
        >
          <span className="py-[2px] font-bold text-center text-xs">{description}</span>
          <div className={cn("absolute -left-[6px] w-[12px] h-full", bgColor)} />
        </div>
      </div>
    </div>
  );
};

const LoadingSkeleton = (props: HTMLAttributes<HTMLDivElement>) => {
  return (
    <div className="min-h-screen w-full bg-blue" {...props}>
      <div className="flex flex-col">
        <div className="h-full w-full pb-3">
          <Skeleton className="w-full h-7 bg-white" />
        </div>
      </div>

      <div className="flex flex-col p-4 bg-white">
        <div>
          <div className="flex items-center gap-3 mb-4">
            <Skeleton className="h-7 w-2/3" />
            <Skeleton className="h-7 w-1/3" />
          </div>
          <div className="flex items-center mb-4">
            <Skeleton className="w-1/3 h-7" />
          </div>
          <div className="flex items-center gap-2 justify-end mb-4">
            <Skeleton className="w-[62px] h-7" />
            <Skeleton className="w-[62px] h-7" />
          </div>

          <div className="flex items-center mb-4">
            <Skeleton className="h-[40px] py-2 w-full" />
          </div>
          <div className="flex items-center">
            <Skeleton className="w-1/3 h-7" />
          </div>
        </div>
      </div>

      {[...Array(4)].map((_, index) => (
        <div key={index} className="flex flex-col p-4 bg-light-blue">
          <div className="flex gap-2 text-[12px]">
            <Skeleton className="w-1/5 h-[100px] py-2" />
            <Skeleton className="w-1/5 h-[100px] py-2" />
            <Skeleton className="w-3/5 h-[100px] py-2" />
          </div>
        </div>
      ))}
    </div>
  );
};
